// "Suscribirme" desde el catálogo del final del onboarding.
//
// Hasta ADR-2026-10-20-suscribirme-del-onboarding-cobra, ese botón hacía lo mismo que
// "Omitir": cerraba el catálogo y mandaba a la home. Era a propósito
// (ADR-2026-07-29-upsell-planes-salir-alta-condicionado: "acá no se cobra" porque la
// persona todavía no tenía empresa), pero esa razón se venció cuando la empresa pasó a
// crearse antes de llegar ahí, y el botón quedó prometiendo algo que no hacía — justo en
// el momento de más ganas de comprar.
//
// Ahora va a `/checkout` igual que el resto de los catálogos (`Paywall`,
// `CreditsPricingSheet`), con una diferencia a propósito: el país y el plan se leen de la
// EMPRESA, no del navegador. `/checkout` asume Argentina si no le llega el país, y
// `paisDelBrowser()` puede devolver null.
//
// Falla cerrado como `CreditsPricingSheet` (ADR-2026-07-31-fail-closed-real-catalogo-enlaces):
// ante cualquier duda —no se pudo leer la empresa, ya tiene un plan, es un plan a medida, no
// tiene país— va a `/subscription`, que resuelve todo eso con su propia consulta (Stripe
// con suscripción activa → portal, plan a medida → su pantalla). Nunca se adivina un cobro.

import { supabase } from './supabase';
import { ULTRA_TIERS } from '../components/pricing/pricingPlans';
import type { PricingPlanKey } from '../components/pricing/pricingContent';
import type { AppliedCoupon } from './hotSaleCoupon';

/** Lo que hace falta saber de la empresa para mandarla a pagar. */
export interface EmpresaParaCobrar {
  pais: string | null;
  plan: string | null;
  planCustomId: string | null;
}

export type DestinoDeSuscripcion =
  | {
      ruta: '/checkout';
      state: { planId: string; pais: string; coupon?: AppliedCoupon; currentPlan: 'gratis' };
    }
  | { ruta: '/subscription' };

/** El plan que entiende `/checkout`: el Ultra va con su tier (500 / 1.000 / 2.000). */
export function planIdParaElCheckout(plan: PricingPlanKey, ultraTierIndex: number): string {
  if (plan !== 'enterprise') return plan;
  const i = Math.min(Math.max(Math.trunc(ultraTierIndex) || 0, 0), ULTRA_TIERS.length - 1);
  return ULTRA_TIERS[i].planId;
}

/**
 * A dónde va "Suscribirme". Pura, para poder testear cada salida.
 *
 * `empresa === null` es "no se pudo leer": falla cerrado.
 */
export function destinoDeSuscripcion(p: {
  plan: PricingPlanKey;
  ultraTierIndex: number;
  empresa: EmpresaParaCobrar | null;
  coupon: AppliedCoupon | null;
}): DestinoDeSuscripcion {
  const { empresa } = p;
  if (!empresa) return { ruta: '/subscription' };
  // Plan a medida: el catálogo público no es para él (ADR-664).
  if (empresa.planCustomId) return { ruta: '/subscription' };
  // Ya paga (o no se sabe qué tiene): un Stripe con suscripción activa que entra a
  // `/checkout` termina con DOS suscripciones (ADR-661). `/subscription` lo manda al portal.
  if (empresa.plan !== 'gratis') return { ruta: '/subscription' };
  const pais = (empresa.pais ?? '').trim();
  if (!pais) return { ruta: '/subscription' };
  return {
    ruta: '/checkout',
    state: {
      planId: planIdParaElCheckout(p.plan, p.ultraTierIndex),
      pais,
      ...(p.coupon ? { coupon: p.coupon } : {}),
      currentPlan: 'gratis',
    },
  };
}

/**
 * Lee la empresa de la sesión. Devuelve `null` ante cualquier error — `.single()` no tira:
 * devuelve `{ data: null, error }`, así que el error se mira a mano en las dos consultas.
 */
export async function leerEmpresaParaCobrar(): Promise<EmpresaParaCobrar | null> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;
    const { data: perfil, error: errorPerfil } = await supabase
      .from('profiles')
      .select('id_company')
      .eq('id', user.id)
      .single();
    const idCompany = (perfil as { id_company?: string | null } | null)?.id_company;
    if (errorPerfil || !idCompany) return null;
    const { data: empresa, error: errorEmpresa } = await supabase
      .from('company_profiles')
      .select('pais, plan, plan_custom_id')
      .eq('id_company', idCompany)
      .single();
    if (errorEmpresa || !empresa) return null;
    const e = empresa as { pais?: string | null; plan?: string | null; plan_custom_id?: string | null };
    return { pais: e.pais ?? null, plan: e.plan ?? null, planCustomId: e.plan_custom_id ?? null };
  } catch {
    return null;
  }
}
